import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
    Typography,
    Grid
} from '@mui/material';
import Menu from '@mui/material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import MenuItem from '@mui/material/MenuItem';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { ManagementActions } from '../../../Services/_actions/Management/management.actions.ts';
import { PaginatedResponse, FilterObj, RoleFormData } from '../../../interfaces/Management.interface.ts';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import CreateRoleForm from './Create.tsx';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useNavigate } from 'react-router';
import { useRecoilValue } from 'recoil';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import useDebounce from '../../Shared/Delay/index.tsx';

const List: React.FC = () => {
    const actions = ManagementActions();
    const [filtersObj, setFiltersObj] = useState<FilterObj>({
        limit: 20,
        search: '',
        page: 0,
        sortOrder: 'desc',
        sortField: 'created_at',
    });
    const navigate = useNavigate();
    const { showSnackBar } = useSnackBar()
    const [roleResponse, setRoleResponse] = useState<PaginatedResponse>({ count: 0, list: [] });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [rowData, setRowData] = useState<RoleFormData | any>({});
    const [isLoading, setIsLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState<null | RoleFormData>(null);
    const [indexToRemove, setIndexToRemove] = useState<number>();
    const loggedInUser = useRecoilValue(userInformationAtom);



    const handleEdit = (data: RoleFormData) => {
        setRowData(data);
        setIsEdit(true);
        setIsModalOpen(true);
    }

    const breadcrumbsData = [
        { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: "/" },
        { label: 'Role' },
    ];
    // const handleDelete = async (data: RoleFormData, indexToRemove: number) => {
    //     try {
    //         let res = await actions.deleteRole(data?.id);
    //         if (res?.status === 200) {
    //             setRoleResponse((prevState) => {
    //                 const updatedList = prevState?.list?.filter((_, index) => index !== indexToRemove);
    //                 return { ...prevState, list: updatedList };
    //             });
    //         }
    //     } catch (error) {
    //         console.log(error);
    //     }
    // };

    const handleDelete = (data: RoleFormData, indexToRemove: number) => {
        setRoleToDelete(data);
        setIndexToRemove(indexToRemove);
        setConfirmOpen(true);
    };
    const handleConfirmDelete = async (confirmed: boolean) => {
        if (confirmed && roleToDelete) {
            try {
                let res = await actions.deleteRole(roleToDelete?.id);
                if (res?.status === 200) {
                    setRoleResponse((prevState) => {
                        const updatedList = prevState?.list?.filter((_, index) => index !== indexToRemove);
                        return { ...prevState, list: updatedList };
                    });
                    showSnackBar('delete role successfully.', 'success');
                }
            } catch (error) {
                showSnackBar('Failed to delete role. Please try again.', 'error');

            }
        }
        setConfirmOpen(false);
    };

    const debouncedSearch = useDebounce(filtersObj.search, 500);
    useEffect(() => {
        if (debouncedSearch || debouncedSearch === '') {
            getRoleList({ ...filtersObj, search: debouncedSearch, page: 0 });
        }
    }, [debouncedSearch]);

    const handleSearch = (e) => {
        setFiltersObj({ ...filtersObj, search: e, page: 0 });
        // getRoleList({ ...filtersObj, search: e, page: 0 }) 
    }

    const columns = useMemo<MRT_ColumnDef<RoleFormData>[]>(
        () => [
            {
                accessorKey: 'index',
                header: 'S.No.',
                enableSorting: false,
                size: 50,
                muiTableHeadCellProps: {
                    sx: {
                        backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px',
                        fontFamily:'system-ui',
                        paddingLeft: '20px !important', // header padding here
                    },
                },
                muiTableBodyCellProps: {
                    sx: {
                        backgroundColor: '#fafafa',
                        paddingLeft: '20px !important',
                    },
                },
                Cell: ({ row }) => filtersObj.page * filtersObj.limit + row.index + 1,
            },
            {
                accessorKey: 'name',
                header: 'Name',
                enableSorting: false,
                size: 150,
            },
            {
                accessorKey: 'codename',
                header: 'Codename',
                enableSorting: false,
                size: 80,
            },
            {
                accessorKey: 'organisation',
                header: 'Organisation',
                enableSorting: false,
                size: 150,
            },
            {
                accessorKey: 'edit',
                header: 'Action',
                enableSorting: false,
                size: 20,
                Cell: ({ row }) => {
                    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
                    const open = Boolean(anchorEl);

                    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                        setAnchorEl(event.currentTarget);
                    };

                    const handleClose = () => {
                        setAnchorEl(null);
                    };

                    return (
                        <div>
                            <IconButton onClick={handleClick}>
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                id={`menu-${row.index}`}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                MenuListProps={{
                                    'aria-labelledby': `menu-button-${row.index}`,
                                }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleEdit(row.original);
                                        handleClose();
                                    }}
                                >
                                    Edit
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleDelete(row.original, row.index);
                                        handleClose();
                                    }}
                                >
                                    Delete
                                </MenuItem>
                            </Menu>
                        </div>
                    );
                },
            }

        ],
        [filtersObj]
    );

    const getRoleList = async (filterObj: FilterObj) => {
        setIsLoading(true);
        try {
            const res = await actions.getRoleList(filterObj);
            setRoleResponse(res?.status === 200 ? res?.data || {} : {});
        } catch (error) {
            showSnackBar('Error fetching roles:', 'error');

        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (data: RoleFormData | null) => {
        if (data) {
            if (isEdit) {
                // Editing an existing role
                let updatedObj = { ...data };
                if (data?.organisation?.organisation_id) {
                    updatedObj = { ...data, organisation: data?.organisation?.organisation_id };
                };
                try {
                    const res = await actions.updateRole(updatedObj);
                    if (res?.status === 200) {
                        setRoleResponse((prevState) => ({
                            ...prevState,
                            list: prevState.list.map((role) =>
                                role.id === res?.data?.data?.id ? res?.data?.data : role
                            ),

                        }));
                        showSnackBar('Role updated successfully!', 'success');
                    }
                } catch (error) {
                    showSnackBar('Failed to update role. Please try again.', 'error');
                }
            } else {
                const { name } = data;
                const { organisation_id } = data.organisation;
                const newObj = { name, organisation: organisation_id };
                try {
                    const res = await actions.postRole(newObj);
                    if (res?.status === 200) {
                        setRoleResponse((prevState) => ({
                            ...prevState,
                            count: prevState.count + 1, // Increment the count
                            list: [...prevState.list, res?.data?.data], // Add the new role to the list
                        }));
                        showSnackBar('Role created successfully!', 'success');
                    }
                } catch (error) {
                    showSnackBar('Failed to create role. Please try again.', 'error');
                }
            }
        }
        setIsModalOpen(false);
    };

    const handleChangePage = (event, newPage) => {
        getRoleList({ ...filtersObj, page: newPage });
        setFiltersObj({ ...filtersObj, page: newPage });
    };
    const handleChangeRowsPerPage = (event) => {
        const value = parseInt(event.target.value) || 20;
        getRoleList({
            ...filtersObj,
            limit: value,
            page: 0,
        });
        setFiltersObj({
            ...filtersObj,
            limit: value,
            page: 0,
        });
    };


    // useEffect(() => {
    //     getRoleList(filtersObj);
    // }, []);

    return (
        <Box sx={{ padding: '5px', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item: { label: string, href: string }) => {
                if (item) {
                    navigate(item?.href);
                }
            }}
            />
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        Role Management
                    </Typography>
                </Grid>
                {/* <Box sx={{ display: 'flex', gap: 2 }}>
                        <SearchBox
                            handleSearch={(e) => { setFiltersObj({ ...filtersObj, search: e, page: 0 }); getRoleList({ ...filtersObj, search: e, page: 0 }) }}
                            value={filtersObj?.search || ''}
                        /> */}
                <Grid item xs={6} textAlign="right">
                    <Button
                        variant="contained"
                        color="primary"
                        size='small'
                        onClick={() => { setIsModalOpen(true); setIsEdit(false); }}
                    >
                        Add
                    </Button>
                </Grid>
            </Grid>
            {/* <Grid container sx={{ alignItems: 'center' }} className='search-box-style'>
                <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
                    <SearchBox
                        handleSearch={handleSearch}
                        value={filtersObj?.search || ''}
                    />
                </Grid>
                {loggedInUser?.is_superuser &&
                    <Grid item xs={12} md={3} textAlign="right" sx={{ pb: 0.7 }}>
                        <OrganisationDropdown
                            value={filtersObj?.selectedOrganisation || null}
                            onChange={(org) => { setFiltersObj({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' }); getRoleList({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' }) }}
                            label={"Select Organisation"}
                        />
                    </Grid>
                }
            </Grid> */}
            <Grid container spacing={{xs:1 , sm:2}} sx={{ alignItems: 'center', mb: {xs:1 , sm:2}, mt:{xs:1, sm:0}}} className='search-box-style'>
  <Grid item xs={12} sm={loggedInUser?.is_superuser ? 9 : 12}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <SearchBox
        handleSearch={handleSearch}
        value={filtersObj?.search || ''}
      />
    </Box>
  </Grid>
  {loggedInUser?.is_superuser && (
    <Grid item xs={12} sm={3}>
      <OrganisationDropdown
        value={filtersObj?.selectedOrganisation || null}
        onChange={(org) => {
          setFiltersObj({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' });
          getRoleList({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' });
        }}
        label={"Select Organisation"}
      />
    </Grid>
  )}
</Grid>

            <MaterialReactTable
                columns={columns}
                data={roleResponse?.list || []}
                muiTableBodyCellProps={{
                    sx: (theme) => ({
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
                    }),
                }}
                muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 390px)', width: '100%' } }}
                muiTableHeadCellProps={{
                    sx: {
                        backgroundImage: 'linear-gradient(0deg, #3e59e3 35%, #5f76e8 100%)',
                        color: 'white',
                       fontWeight: '600',
                       fontSize: '14px',
                       fontFamily: 'system-ui',
                    },
                }}
                state={{
                    showColumnFilters: false,
                    isLoading
                }}
                enableDensityToggle={false}
                enableFullScreenToggle={false}
                enableColumnActions={false}
                enableColumnFilters={false}
                enableHiding={false}
                enableStickyHeader={true}
                enableTopToolbar={false}
                initialState={{
                    density: 'comfortable',
                }}
                enablePagination={false}
                manualPagination={false}
                renderBottomToolbar={() => (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
                        <Pagination
                            totalCount={roleResponse?.count || 0}
                            rowsPerPage={filtersObj?.limit}
                            page={filtersObj?.page}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Box>
                )}
            />
            {isModalOpen && (
                <CreateRoleForm
                    open={isModalOpen}
                    onClose={handleSubmit}
                    data={rowData}
                    mode={isEdit}
                />

            )}
            <ConfirmBox
                open={confirmOpen}
                onClose={handleConfirmDelete}
                options={{
                    title: 'Delete Role',
                    text: 'Are you sure you want to delete this role?',
                    confirmButtonText: 'Yes',
                    cancelButtonText: 'No',
                    confirmButtonColor: 'error',
                    cancelButtonColor: 'default',
                }}
            />

        </Box>
    );
};

export default List;
